/*
  AMAVO CLINICAL — homepage
  ------------------------------------------------------------------
  One behaviour only: deciding whether the hero video is allowed to play.
*/

(() => {
  'use strict';

  /* A background video is a cost the visitor pays in data and battery.
     It plays only when three things are true: they have not asked for
     reduced motion, the connection is not slow or metered, and the hero
     is actually on screen. Otherwise the poster image stands alone —
     which is why the poster is required, not optional. */

  class AmvHeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('[data-amv-hero-video]');
      if (!this.video) return;

      if (!this.allowed()) return;

      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.video.play().catch(() => { /* autoplay refused: poster stays */ });
        } else {
          this.video.pause();
        }
      }, { threshold: 0.1 });

      observer.observe(this);
      this.observer = observer;
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    allowed() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

      const net = navigator.connection;
      if (net) {
        if (net.saveData) return false;
        if (['slow-2g', '2g', '3g'].includes(net.effectiveType)) return false;
      }
      return true;
    }
  }

  if (!customElements.get('amv-hero-video')) customElements.define('amv-hero-video', AmvHeroVideo);

  /* ---------------------------------------------------------------
     PARALAXA EROULUI

     Fotografia se misca mai incet decat pagina. Scara de 1.14 e pusa in
     CSS, deci nu exista salt intre primul cadru si momentul in care
     scriptul preia controlul, iar daca scriptul nu ruleaza fotografia
     ramane doar incadrata mai strans.

     Trei lucruri o opresc: cererea de mai putina miscare, iesirea din
     ecran (IntersectionObserver) si lipsa unui strat de miscat.
     --------------------------------------------------------------- */

  const AMPLITUDINE = 0.06;   // din inaltimea eroului; scara lasa 0.07 la fiecare capat

  class AmvParallax {
    constructor(host) {
      this.host = host;
      this.layers = Array.from(host.querySelectorAll('.amv-hero__img, .amv-hero__video'));
      if (!this.layers.length) return;

      this.vizibil = false;
      this.ticheta = false;

      this.observer = new IntersectionObserver(([entry]) => {
        this.vizibil = entry.isIntersecting;
        if (this.vizibil) this.cere();
      }, { rootMargin: '120px 0px' });
      this.observer.observe(host);

      this.onScroll = () => this.cere();
      addEventListener('scroll', this.onScroll, { passive: true });
      addEventListener('resize', this.onScroll, { passive: true });
      this.aseaza();
    }

    cere() {
      if (this.ticheta || !this.vizibil) return;
      this.ticheta = true;
      requestAnimationFrame(() => { this.aseaza(); this.ticheta = false; });
    }

    aseaza() {
      const r = this.host.getBoundingClientRect();
      if (!r.height) return;
      const vh = innerHeight || document.documentElement.clientHeight;

      // -1 cand sectiunea tocmai intra de jos, +1 cand tocmai a iesit pe sus.
      const brut = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
      const p = Math.max(-1, Math.min(1, brut));
      const y = (p * AMPLITUDINE * r.height).toFixed(2);

      this.layers.forEach((el) => {
        el.style.transform = `scale(1.14) translate3d(0, ${y}px, 0)`;
      });
    }
  }

  function porneste(radacina) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    (radacina || document).querySelectorAll('[data-amv-parallax]').forEach((el) => {
      if (el.amvParallax) return;
      el.amvParallax = new AmvParallax(el);
    });
  }

  porneste();
  // Editorul de teme re-randeaza sectiunea fara sa reincarce pagina.
  document.addEventListener('shopify:section:load', (e) => porneste(e.target));
})();
